import Ticket from 'rf-ui/GameRenderer/entities/ticket';
import Two from 'two.js';
import { Group } from 'two.js/src/group';
import { ArcSegment } from 'two.js/src/shapes/arc-segment';
import { Line } from 'two.js/src/shapes/line';
import { WHEEL_RADIUS } from 'rf-ui/GameRenderer/wheelOfFortuneConstants';
import ribbonTie from '../../../assets/images/ribbon-tie.svg';

export default class WrappedTicket extends Ticket {
  private static readonly CROSS_RADIUS = WHEEL_RADIUS * 0.6;
  private static readonly RIBBON_WIDTH = 18;
  private static readonly TIE_SIZE = 11;
  private static readonly SCALE_START_WIDTH = 0.2;
  private static readonly SCALE_END_WIDTH = 0.1;
  private static readonly MIN_RIBBON_SCALE = 0.3;
  private static readonly RIBBON_FILL = '#ff77b7';
  private static readonly RIBBON_STROKE = '#c23a7c';
  private ribbonGroup: Group;
  private ribbonMask: ArcSegment;
  private centerRibbonOutline: Line;
  private crossRibbonOutline: Line;
  private centerRibbon: Line;
  private crossRibbon: Line;
  private ribbonTie: any;

  constructor(two: Two, startAngle: number, width: number) {
    super(two, startAngle, width, 'red', '', false);

    const wrapTexture = Ticket.getRandomWrapTexture(two);

    this.background.fill = wrapTexture;

    this.ribbonGroup = new Group();
    this.ribbonMask = two.makeArcSegment(0, 0, 0, WHEEL_RADIUS, startAngle, startAngle + width);
    this.centerRibbonOutline = two.makeLine(0, 0, 0, 0);
    this.crossRibbonOutline = two.makeLine(0, 0, 0, 0);
    this.centerRibbon = two.makeLine(0, 0, 0, 0);
    this.crossRibbon = two.makeLine(0, 0, 0, 0);
    this.ribbonTie = two.makeSprite(ribbonTie, 0, 0, 1, 1, 0, false);

    [this.centerRibbonOutline, this.crossRibbonOutline].forEach(ribbon => {
      ribbon.stroke = WrappedTicket.RIBBON_STROKE;
      ribbon.linewidth = WrappedTicket.RIBBON_WIDTH + 6;
      ribbon.cap = 'butt';
      ribbon.join = 'miter';
      ribbon.noFill();
      this.ribbonGroup.add(ribbon);
    });
    [this.centerRibbon, this.crossRibbon].forEach(ribbon => {
      ribbon.stroke = WrappedTicket.RIBBON_FILL;
      ribbon.linewidth = WrappedTicket.RIBBON_WIDTH;
      ribbon.cap = 'butt';
      ribbon.join = 'miter';
      ribbon.noFill();
      this.ribbonGroup.add(ribbon);
    });
    this.ribbonTie.scale = WrappedTicket.TIE_SIZE / 24;
    this.ribbonGroup.add(this.ribbonTie);

    this.ribbonMask.fill = '#fff';
    this.ribbonMask.stroke = 'none';
    this.ribbonGroup.add(this.ribbonMask);
    this.ribbonGroup.mask = this.ribbonMask;
    this.add(this.ribbonGroup);

    this.updateRibbonLayout(startAngle, width);
  }

  public massCentered() {
    super.massCentered();
    this.updateRibbonLayout(this.startAngle, this.width);
    return this;
  }

  public resize(rotation: number, width: number) {
    super.resize(rotation, width);
    this.updateRibbonLayout(rotation, width);
  }

  private updateRibbonLayout(rotation: number, width: number) {
    const centerAngle = rotation + (width / 2);
    const directionX = Math.cos(centerAngle);
    const directionY = Math.sin(centerAngle);
    const tangentX = -Math.sin(centerAngle);
    const tangentY = Math.cos(centerAngle);
    const crossCenterX = directionX * WrappedTicket.CROSS_RADIUS;
    const crossCenterY = directionY * WrappedTicket.CROSS_RADIUS;
    const crossHalfLength = WHEEL_RADIUS;
    const localX = this.background.translation.x;
    const localY = this.background.translation.y;
    const ribbonScale = this.getRibbonScale(width);

    this.ribbonMask.translation.set(localX, localY);
    this.centerRibbonOutline.translation.set(localX, localY);
    this.crossRibbonOutline.translation.set(localX, localY);
    this.centerRibbon.translation.set(localX, localY);
    this.crossRibbon.translation.set(localX, localY);

    this.centerRibbonOutline.linewidth = (WrappedTicket.RIBBON_WIDTH + 6) * ribbonScale;
    this.crossRibbonOutline.linewidth = (WrappedTicket.RIBBON_WIDTH + 6) * ribbonScale;
    this.centerRibbon.linewidth = WrappedTicket.RIBBON_WIDTH * ribbonScale;
    this.crossRibbon.linewidth = WrappedTicket.RIBBON_WIDTH * ribbonScale;

    this.ribbonMask.innerRadius = 0;
    this.ribbonMask.outerRadius = WHEEL_RADIUS;
    this.ribbonMask.startAngle = rotation;
    this.ribbonMask.endAngle = rotation + width;

    this.centerRibbon.vertices[0].set(
      directionX * (-WHEEL_RADIUS * 0.15),
      directionY * (-WHEEL_RADIUS * 0.15)
    );
    this.centerRibbon.vertices[1].set(
      directionX * (WHEEL_RADIUS * 1.05),
      directionY * (WHEEL_RADIUS * 1.05)
    );

    this.crossRibbon.vertices[0].set(
      crossCenterX - (tangentX * crossHalfLength),
      crossCenterY - (tangentY * crossHalfLength)
    );
    this.crossRibbon.vertices[1].set(
      crossCenterX + (tangentX * crossHalfLength),
      crossCenterY + (tangentY * crossHalfLength)
    );

    this.centerRibbonOutline.vertices[0].copy(this.centerRibbon.vertices[0]);
    this.centerRibbonOutline.vertices[1].copy(this.centerRibbon.vertices[1]);
    this.crossRibbonOutline.vertices[0].copy(this.crossRibbon.vertices[0]);
    this.crossRibbonOutline.vertices[1].copy(this.crossRibbon.vertices[1]);

    this.ribbonTie.translation.set(localX + crossCenterX, localY + crossCenterY);
    this.ribbonTie.rotation = centerAngle + (Math.PI / 2);
    this.ribbonTie.scale = (WrappedTicket.TIE_SIZE / 24) * ribbonScale;
  }

  private getRibbonScale(width: number) {
    if (width >= WrappedTicket.SCALE_START_WIDTH) return 1;
    if (width <= WrappedTicket.SCALE_END_WIDTH) return WrappedTicket.MIN_RIBBON_SCALE;

    const progress = (width - WrappedTicket.SCALE_END_WIDTH)
      / (WrappedTicket.SCALE_START_WIDTH - WrappedTicket.SCALE_END_WIDTH);

    return WrappedTicket.MIN_RIBBON_SCALE
      + ((1 - WrappedTicket.MIN_RIBBON_SCALE) * progress);
  }
}
